import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { api, FetchError } from '../api.ts';
import type { BriefImage } from '@sab/core/schemas';

/**
 * BriefImageDropzone — drag/drop or click to upload up to 10 brief
 * screenshots (WA chat, email, marketing material). Per-image client-side
 * resize to max 1280px before upload (canvas API) to keep payload + vision
 * cost predictable. Optimistic preview thumbnails with progress + error
 * states.
 *
 * Editorial-luxury vocabulary: warm dashed border, orange accent on
 * drag-over, mono filenames, restrained typography. Touch targets 44×44px
 * on mobile.
 *
 * Auto-extract pipeline (Stage 2) consumes `value` array to prefill form
 * fields after every successful upload.
 */

export interface BriefImageDropzoneProps {
  /** Current uploaded images (server-confirmed via R2 key). */
  value: BriefImage[];
  /** Called whenever the set changes (add/remove). Caller persists to
   *  intake.briefImages + sessionStorage.  */
  onChange: (next: BriefImage[]) => void;
  /** Fires when at least one new image was just uploaded.
   *  Receives BOTH the newly-uploaded images AND the full current set
   *  (so the parent never has to read stale state for follow-up actions
   *  like auto-extract). */
  onUploadsCompleted?: (args: { newImages: BriefImage[]; allImages: BriefImage[] }) => void;
  /** Disable the whole dropzone (e.g. during form submit). */
  disabled?: boolean;
}

const MAX_IMAGES = 10;
const MAX_RESIZE_DIM = 1280;
const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];

/** A pending upload — local-only until R2 confirms. */
interface PendingUpload {
  localId: string;
  name: string;
  bytes: number;
  preview: string; // blob: URL
  progress: number;
  error?: string;
}

export function BriefImageDropzone({
  value,
  onChange,
  onUploadsCompleted,
  disabled,
}: BriefImageDropzoneProps) {
  const fileInputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState<PendingUpload[]>([]);

  // Revoke blob URLs on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      pending.forEach((p) => URL.revokeObjectURL(p.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalCount = value.length + pending.length;
  const canAddMore = totalCount < MAX_IMAGES && !disabled;
  const slotsLeft = MAX_IMAGES - totalCount;

  /** Client-side resize via canvas. JPEG output at 0.86 quality. */
  const resizeImage = useCallback(async (file: File): Promise<Blob> => {
    if (file.size < 200_000) return file; // small enough — skip resize
    const bitmap = await createImageBitmap(file);
    const ratio = Math.min(MAX_RESIZE_DIM / bitmap.width, MAX_RESIZE_DIM / bitmap.height, 1);
    if (ratio >= 0.99) return file;
    const w = Math.round(bitmap.width * ratio);
    const h = Math.round(bitmap.height * ratio);
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await canvas.convertToBlob({
      type: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
      quality: 0.86,
    });
    bitmap.close();
    return blob;
  }, []);

  const uploadOne = useCallback(
    async (file: File): Promise<BriefImage | null> => {
      const localId = crypto.randomUUID();
      const preview = URL.createObjectURL(file);
      setPending((p) => [
        ...p,
        { localId, name: file.name, bytes: file.size, preview, progress: 10 },
      ]);

      try {
        // Resize first (offload heavy compute to OffscreenCanvas)
        setPending((p) => p.map((it) => (it.localId === localId ? { ...it, progress: 35 } : it)));
        const blob = await resizeImage(file);
        const sized =
          blob instanceof File ? blob : new File([blob], file.name, { type: blob.type });
        setPending((p) => p.map((it) => (it.localId === localId ? { ...it, progress: 65 } : it)));
        const res = await api.uploadBriefImage(sized);
        setPending((p) => p.filter((it) => it.localId !== localId));
        URL.revokeObjectURL(preview);
        return {
          key: res.key,
          contentType: res.contentType as BriefImage['contentType'],
          name: res.name,
          bytes: res.bytes,
        };
      } catch (err) {
        const message = err instanceof FetchError ? err.body.error : 'Upload gagal';
        setPending((p) =>
          p.map((it) => (it.localId === localId ? { ...it, error: message, progress: 0 } : it)),
        );
        // Keep error visible 4s, then auto-clear
        setTimeout(() => {
          setPending((p) => p.filter((it) => it.localId !== localId));
          URL.revokeObjectURL(preview);
        }, 4000);
        return null;
      }
    },
    [resizeImage],
  );

  const ingestFiles = useCallback(
    async (files: File[]) => {
      if (!canAddMore) return;
      const accepted = files
        .filter((f) => ACCEPT.includes(f.type))
        .filter((f) => f.size <= 20 * 1024 * 1024) // 20MB raw cap before resize
        .slice(0, slotsLeft);

      if (accepted.length === 0) return;

      const results = await Promise.all(accepted.map(uploadOne));
      const newImages = results.filter((r): r is BriefImage => r !== null);
      if (newImages.length > 0) {
        const next = [...value, ...newImages];
        onChange(next);
        // Pass both newly-uploaded AND full set so consumer (Intake)
        // doesn't have to wait for state to flush before firing the
        // auto-extract API call.
        onUploadsCompleted?.({ newImages, allImages: next });
      }
    },
    [canAddMore, slotsLeft, uploadOne, value, onChange, onUploadsCompleted],
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      void ingestFiles(Array.from(e.target.files));
      e.target.value = ''; // allow re-pick same file
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (!canAddMore) return;
    void ingestFiles(Array.from(e.dataTransfer.files));
  };

  const handleRemove = async (key: string) => {
    onChange(value.filter((img) => img.key !== key));
    try {
      await api.deleteBriefImage(key);
    } catch {
      /* server-side delete is best-effort; lifecycle rule will sweep */
    }
  };

  return (
    <div className="dz" aria-label="Upload screenshot brief prospek">
      <label
        htmlFor={fileInputId}
        className={[
          'dz__zone',
          dragOver ? 'dz__zone--drag' : '',
          !canAddMore ? 'dz__zone--full' : '',
          disabled ? 'dz__zone--disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onDragOver={(e) => {
          if (!canAddMore) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          id={fileInputId}
          type="file"
          multiple
          accept={ACCEPT.join(',')}
          onChange={handleFileInput}
          disabled={!canAddMore}
          className="dz__input"
        />
        <div className="dz__inner">
          <svg
            viewBox="0 0 24 24"
            width="28"
            height="28"
            fill="none"
            aria-hidden="true"
            className="dz__icon"
          >
            <path
              d="M12 16V4M12 4l-5 5M12 4l5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="dz__copy">
            <strong className="dz__title">
              Drop screenshot WhatsApp / email — AI extract otomatis
            </strong>
            <span className="dz__hint">
              {canAddMore
                ? `Maksimal ${MAX_IMAGES} gambar · JPG / PNG / WEBP · sampai 5 MB per file · ${slotsLeft} slot tersisa`
                : `Slot penuh (${MAX_IMAGES}/${MAX_IMAGES}). Hapus salah satu untuk tambah baru.`}
            </span>
          </div>
        </div>
      </label>

      {(value.length > 0 || pending.length > 0) && (
        <div className="dz__count">
          <span className="dz__count-num">
            {value.length}
            <span className="dz__count-slash">/{MAX_IMAGES}</span>
          </span>
          <span className="dz__count-label">terupload</span>
          {pending.length > 0 ? (
            <span className="dz__count-pending">+ {pending.length} memproses…</span>
          ) : null}
        </div>
      )}

      {(value.length > 0 || pending.length > 0) && (
        <ul className="dz__grid" aria-label="Daftar screenshot terupload">
          {value.map((img) => (
            <li key={img.key} className="dz__tile dz__tile--done">
              <img
                src={`/proposal/api/r2-asset?key=${encodeURIComponent(img.key)}`}
                alt={img.name ?? 'Brief screenshot'}
                className="dz__thumb"
                loading="lazy"
                decoding="async"
              />
              <div className="dz__tile-foot">
                <span className="dz__tile-name" title={img.name}>
                  {img.name ?? img.key.split('/').pop()}
                </span>
                <button
                  type="button"
                  className="dz__remove"
                  onClick={() => handleRemove(img.key)}
                  aria-label={`Hapus ${img.name ?? 'gambar'}`}
                  disabled={disabled}
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
                    <path
                      d="M6 6l12 12M6 18l12-12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </li>
          ))}
          {pending.map((p) => (
            <li
              key={p.localId}
              className={`dz__tile ${p.error ? 'dz__tile--error' : 'dz__tile--pending'}`}
            >
              <img
                src={p.preview}
                alt={p.name}
                className="dz__thumb dz__thumb--pending"
                loading="lazy"
              />
              {p.error ? (
                <div className="dz__tile-error">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                    <path
                      d="M12 7v6M12 16.5h.01"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>{p.error}</span>
                </div>
              ) : (
                <div className="dz__progress" aria-label={`Upload ${p.progress}%`}>
                  <div className="dz__progress-bar" style={{ width: `${p.progress}%` }} />
                </div>
              )}
              <div className="dz__tile-foot">
                <span className="dz__tile-name" title={p.name}>
                  {p.name}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
